/**
 * AADHYAA — Lazy-Loaded Page Components
 * =====================================
 * Centralized React.lazy() registry for all route-level page components.
 *
 * Why this file?
 *   App.js used to be 877 lines, with ~120 of them devoted to lazy()
 *   declarations. Lifting that block here shrinks App.js to its essence
 *   (providers + the JSX route tree) and lets us evolve the page list
 *   without touching the routing layout.
 *
 * Organisation (Phase 19.2.2 — feature-folder grouping):
 *   1. PUBLIC — discoverability / SEO surfaces (anonymous)
 *   2. ONBOARDING & AUTH — first-launch + recovery
 *   3. CORE 7-SECTION ARCHITECTURE — primary post-login experience
 *   4. AI & CHAT — Ask AADHYAA + intelligence surfaces
 *   5. TIMING & MUHURTA — decision-timing tools
 *   6. KUNDLI & CHARTS — natal-chart deep dives
 *   7. RELATIONSHIPS & FAMILY — compatibility, family intel
 *   8. WELLNESS & PRACTICES — habits, mantras, ritual, mood
 *   9. REPORTS & PURCHASES — 12-report system + history
 *  10. COMMERCE — pricing, subscriptions, sharing
 *  11. LEGAL & REFERENCE — policy, methodology, glossary
 *  12. ADMIN & ANALYTICS — internal dashboards
 *  13. INSIGHTS & EDITORIAL — content/SEO articles
 *
 * Each export below is wrapped in React.lazy() — the actual module is
 * fetched on first render, keeping the critical-path bundle small.
 */
import { lazy } from "react";

// ============================================================================
// 1) PUBLIC — discoverability / SEO (anonymous browsing)
// ============================================================================
export const AboutPage = lazy(() => import("./pages/AboutPage"));
export const PricingPage = lazy(() => import("./pages/PricingPage"));
export const LegalPage = lazy(() => import("./pages/LegalPage"));
export const TermsPage = lazy(() => import("./pages/TermsPage"));
export const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage.jsx"));
// SEO landing pages (Phase 16 Move 3b/3c)
export const PublicNakshatraPage = lazy(() => import("./pages/PublicNakshatraPage.jsx"));
export const PublicRashiPage = lazy(() => import("./pages/PublicRashiPage.jsx"));
export const PublicLearnListPages = lazy(() => import("./pages/PublicLearnListPages.jsx").then(m => ({ default: m.PublicRashiListPage })));
export const PublicNakshatraListPage = lazy(() => import("./pages/PublicLearnListPages.jsx").then(m => ({ default: m.PublicNakshatraListPage })));
export const PublicKundliPage = lazy(() => import("./pages/PublicKundliPage.jsx"));
export const PublicCompatibilityPage = lazy(() => import("./pages/PublicCompatibilityPage.jsx"));

// ============================================================================
// 2) ONBOARDING & AUTH RECOVERY
// ============================================================================
export const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
export const ProfileCompletePage = lazy(() => import("./pages/ProfileCompletePage"));
export const ReviewerLoginPage = lazy(() => import("./pages/ReviewerLoginPage"));
export const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
export const GettingStartedPage = lazy(() => import("./pages/GettingStartedPage")); // Vedic 101

// ============================================================================
// 3) CORE 7-SECTION ARCHITECTURE — primary post-login experience
// ============================================================================
export const HomePage = lazy(() => import("./pages/HomePage"));
export const MyLifePage = lazy(() => import("./pages/MyLifePage"));
export const TimingPage = lazy(() => import("./pages/TimingPage"));
export const RelationshipsPage = lazy(() => import("./pages/RelationshipsPage"));
export const LearnGrowPage = lazy(() => import("./pages/LearnGrowPage"));
export const ReportsPage = lazy(() => import("./pages/ReportsPageNew")); // 12-Report system
export const DashboardPage = lazy(() => import("./pages/DashboardPage"));
export const JourneyDashboard = lazy(() => import("./pages/JourneyDashboard")); // legacy /today
export const MyStarsPage = lazy(() => import("./pages/InsightsPage.js")); // legacy /my-stars
export const WisdomHubPage = lazy(() => import("./pages/WisdomHubPage"));

// ============================================================================
// 4) AI & CHAT — Ask AADHYAA + intelligence surfaces
// ============================================================================
export const AskPage = lazy(() => import("./pages/AskPage"));
export const AstroIntelligencePage = lazy(() => import("./pages/AstroIntelligencePage"));
export const AIIntelligencePage = lazy(() => import("./pages/AIIntelligencePage"));
export const VerifiedReadingPage = lazy(() => import("./pages/VerifiedReadingPage"));
export const MyLifeReadingsPage = lazy(() => import("./pages/MyLifeReadingsPage"));

// ============================================================================
// 5) TIMING & MUHURTA — decision-timing tools
// ============================================================================
export const PanchangPage = lazy(() => import("./pages/PanchangPage"));
export const MuhurtaPage = lazy(() => import("./pages/MuhurtaPage"));
export const MuhurtaComparePage = lazy(() => import("./pages/MuhurtaComparePage"));
export const HoroscopePage = lazy(() => import("./pages/HoroscopePage"));
export const EkadashiPage = lazy(() => import("./pages/EkadashiPage"));
export const DashaExplorerPage = lazy(() => import("./pages/DashaExplorerPage"));
export const PrashnaVarshphalPage = lazy(() => import("./pages/PrashnaVarshphalPage"));

// ============================================================================
// 6) KUNDLI & CHARTS — natal-chart deep dives
// ============================================================================
export const KundliPage = lazy(() => import("./pages/KundliPage"));
export const VedicDepthHub = lazy(() => import("./pages/VedicDepthHub"));
export const LifePathPage = lazy(() => import("./pages/LifePathPage"));
export const TrustDashboardPage = lazy(() => import("./pages/TrustDashboardPage"));

// ============================================================================
// 7) RELATIONSHIPS & FAMILY — compatibility, family intel
// ============================================================================
export const FamilyPage = lazy(() => import("./pages/FamilyPageV2"));
export const FamilyDashboardPage = lazy(() => import("./pages/FamilyDashboardPage"));
export const FamilyIntelligencePage = lazy(() => import("./pages/FamilyIntelligencePage"));
export const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));
export const MarriageCompatibilityPage = lazy(() => import("./pages/MarriageCompatibilityPage"));

// ============================================================================
// 8) WELLNESS & PRACTICES — habits, mantras, ritual, mood
// ============================================================================
export const HabitsPage = lazy(() => import("./pages/HabitsPage"));
export const MantrasPage = lazy(() => import("./pages/MantrasPage"));
export const RitualReminderPage = lazy(() => import("./pages/RitualReminderPage"));
export const GuidedRitualsPage = lazy(() => import("./pages/GuidedRitualsPage"));
export const RemediesPage = lazy(() => import("./pages/RemediesPage"));
export const MoodJournalPage = lazy(() => import("./pages/MoodJournalPage"));
export const CircadianPage = lazy(() => import("./pages/CircadianPage"));
export const HealthConnectionPage = lazy(() => import("./pages/HealthConnectionPage"));
export const KidsCoursesPage = lazy(() => import("./pages/KidsCoursesPage"));
export const ChecklistPage = lazy(() => import("./pages/ChecklistPage"));
export const KnowledgeHubPage = lazy(() => import("./pages/KnowledgeHubPage"));
export const LearnPage = lazy(() => import("./pages/LearnPage"));
export const ToolsPage = lazy(() => import("./pages/ToolsPage"));

// ============================================================================
// 9) REPORTS & PURCHASES — 12-report system + history
// ============================================================================
export const ReportViewPage = lazy(() => import("./pages/ReportViewPage"));
export const ReportHubPage = lazy(() => import("./pages/ReportHubPage"));
export const ReportsCatalogPage = lazy(() => import("./pages/ReportsCatalogPage"));
export const MyPurchasesPage = lazy(() => import("./pages/MyPurchasesPage"));
export const PurchaseHistoryPage = lazy(() => import("./pages/PurchaseHistoryPage"));
export const AstroReportPage = lazy(() => import("./pages/AstroReportPage"));

// ============================================================================
// 10) COMMERCE — subscriptions, sharing, consultations
// ============================================================================
export const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
export const SubscriptionCancelPage = lazy(() => import("./pages/SubscriptionCancelPage"));
export const SharePage = lazy(() => import("./pages/SharePage"));
export const ConsultationPage = lazy(() => import("./pages/ConsultationPage"));
export const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
export const SettingsPage = lazy(() => import("./pages/SettingsPage"));
export const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));

// ============================================================================
// 11) LEGAL & REFERENCE — policy, methodology, glossary
// ============================================================================
export const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
export const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
export const PrivacyDashboardPage = lazy(() => import("./pages/PrivacyDashboardPage"));

// ============================================================================
// 12) ADMIN & ANALYTICS — internal dashboards
// ============================================================================
export const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
export const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage.jsx"));
export const SLADashboardPage = lazy(() => import("./pages/SLADashboardPage.jsx"));
export const AccuracyDashboard = lazy(() => import("./pages/AccuracyDashboard"));
export const BetaAdminDashboard = lazy(() => import("./pages/BetaAdminDashboard"));
export const EnterpriseAdminDashboard = lazy(() => import("./pages/AdminDashboard"));
export const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
export const ConversionDashboard = lazy(() => import("./pages/ConversionDashboard"));

// ============================================================================
// 13) INSIGHTS & EDITORIAL — content/SEO articles
// ============================================================================
export const InsightsPage = lazy(() => import("./pages/InsightsPage.jsx"));
export const InsightsPillarPage = lazy(() => import("./pages/InsightsPillarPage"));
export const InsightsArticlePage = lazy(() => import("./pages/InsightsArticlePage"));
